// OperatorCareersHelper.jsx
import React, {
  Fragment,
  useMemo,
  useState
} from "react";

import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform
} from "framer-motion";

import {
  ArrowRight,
  BadgeCheck,
  BriefcaseBusiness,
  Check,
  ChevronRight,
  Code2,
  Compass,
  FileCheck2,
  Fingerprint,
  GitBranch,
  Globe2,
  Handshake,
  HeartHandshake,
  KeyRound,
  Layers3,
  Network,
  Orbit,
  Rocket,
  Scale,
  Server,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
  Waypoints,
  Workflow,
  X
} from "lucide-react";

import "./Styles.css";
import HeaderToMainPage from "../Universal/Header/Index";
import Footer from "../Universal/Footer/Index";
import { Link } from "react-router-dom";


/* ============================================================
   CONTENT
============================================================ */

const roleTracks = [
  {
    id: "systems",
    number: "01",
    accent: "cyan",
    icon: Server,
    badge: "CORE ENGINEERING",
    title: "Backend Infrastructure Engineer",
    tagline: "Own the parts that cannot be allowed to lie.",
    summary:
      "Work on server-authoritative game execution, tenant boundaries, wallet and settlement integrity, observability, and the systems that make Ethosino predictable under real operator load.",
    influence: [
      "State-machine and concurrency correctness",
      "Wallet, ledger and settlement integrity",
      "Tenant isolation and entitlement enforcement",
      "Operational telemetry and failure visibility",
      "Performance behavior under production pressure"
    ],
    signals: [
      ["MINDSET", "systems-first"],
      ["FOCUS", "correctness"],
      ["SCOPE", "core runtime"]
    ],
    lookingFor: [
      "Strong backend and distributed-systems instincts",
      "Comfort reasoning about state, failure and idempotency",
      "Ability to make complex behavior observable",
      "Bias toward explicit invariants over clever abstractions"
    ],
    collaboration:
      "Foundational technical contributor. The goal is not ticket throughput; it is shared ownership of system boundaries, architecture and disciplined delivery.",
    cta: "Discuss engineering role",
    href: "/contact"
  },
  {
    id: "legal",
    number: "02",
    accent: "violet",
    icon: Scale,
    badge: "LEGAL + COMPLIANCE",
    title: "Gaming / FinTech Legal & Compliance Advisor",
    tagline: "Translate legal constraints into system boundaries.",
    summary:
      "Help shape the legal and compliance posture around operator deployment, jurisdictional boundaries, policy enforcement, diligence materials and the evidence story the platform must support.",
    influence: [
      "Jurisdiction and operating-model analysis",
      "Product and software-boundary language",
      "Compliance workflow and governance design",
      "Operator onboarding and diligence posture",
      "Audit narrative and documentation quality"
    ],
    signals: [
      ["MINDSET", "defensible"],
      ["FOCUS", "boundaries"],
      ["SCOPE", "operator posture"]
    ],
    lookingFor: [
      "Gaming, sweepstakes, payments or regulated-platform experience",
      "Ability to turn legal requirements into operational controls",
      "Comfort with software architecture and evidence",
      "Clear, practical communication across technical and commercial teams"
    ],
    collaboration:
      "Advisory or strategic engagement with defined scope and cadence. Long-term alignment matters more than producing one memo and disappearing.",
    cta: "Discuss advisory role",
    href: "/contact"
  },
  {
    id: "growth",
    number: "03",
    accent: "green",
    icon: Handshake,
    badge: "GROWTH + PARTNERSHIPS",
    title: "Operator Growth & Partnerships Lead",
    tagline: "Turn a deep product into a repeatable operator motion.",
    summary:
      "Build the bridge between the platform and the market: operator pipeline, positioning, partnerships, onboarding, commercial conversations and the feedback loop that keeps GTM connected to product reality.",
    influence: [
      "Operator acquisition and qualification",
      "Positioning and market narrative",
      "Partnership and distribution strategy",
      "Pilot and onboarding motion",
      "Commercial feedback into roadmap decisions"
    ],
    signals: [
      ["MINDSET", "operator-first"],
      ["FOCUS", "distribution"],
      ["SCOPE", "GTM system"]
    ],
    lookingFor: [
      "B2B gaming, SaaS, payments or infrastructure GTM experience",
      "Comfort selling a technical product without flattening it",
      "Strong operator or ecosystem relationship instincts",
      "Ability to build process before there is a giant sales team"
    ],
    collaboration:
      "Foundational GTM role with meaningful ownership of the commercial system. Performance-aligned structures can be discussed based on scope and commitment.",
    cta: "Discuss GTM role",
    href: "/contact"
  },
  {
    id: "capital",
    number: "04",
    accent: "pink",
    icon: Rocket,
    badge: "STRATEGIC CAPITAL",
    title: "Angel / Seed Partner",
    tagline: "Capital is useful. Leverage is better.",
    summary:
      "Ethosino is open to aligned capital partners who understand infrastructure businesses and can improve the odds of execution through operator access, regulated-market context, talent or strategic pattern recognition.",
    influence: [
      "Execution runway and hiring capacity",
      "Operator and partner introductions",
      "Strategic diligence and positioning",
      "Regulated-market network access",
      "Long-term commercial and capital discipline"
    ],
    signals: [
      ["MINDSET", "long-term"],
      ["FOCUS", "leverage"],
      ["SCOPE", "company scale"]
    ],
    lookingFor: [
      "Infrastructure or regulated-software pattern recognition",
      "Comfort evaluating deep product before hypergrowth metrics",
      "Practical network value beyond capital",
      "Alignment with product integrity and disciplined scaling"
    ],
    collaboration:
      "Selective capital conversations. The objective is durable alignment around the company Ethosino is actually building — not growth theater.",
    cta: "Request investor conversation",
    href: "/contact"
  }
];


const principles = [
  {
    icon: Compass,
    title: "Think in systems.",
    body:
      "A feature is never just a feature here. It touches state, money, permissions, evidence, operator workflows or player trust."
  },
  {
    icon: Fingerprint,
    title: "Make behavior explainable.",
    body:
      "If a consequential system cannot be explained after the fact, it is not finished."
  },
  {
    icon: ShieldCheck,
    title: "Protect the boundaries.",
    body:
      "Tenant context, financial authority, fairness material and operator permissions are architectural concerns, not UI details."
  },
  {
    icon: Workflow,
    title: "Ship without creating debt traps.",
    body:
      "Velocity matters, but not when every shortcut becomes an invisible dependency the next operator has to inherit."
  },
  {
    icon: HeartHandshake,
    title: "Respect the operator and player.",
    body:
      "The platform should make operating easier without turning trust, fairness or player well-being into disposable tradeoffs."
  },
  {
    icon: Target,
    title: "Prefer evidence over theater.",
    body:
      "We care more about what the system does under pressure than how impressive a deck sounds."
  }
];


const stageCards = [
  {
    number: "01",
    icon: Code2,
    title: "The product exists.",
    body:
      "This is not an idea-stage recruiting pitch. The core platform, operator surfaces and first-party systems already exist; new contributors are joining something they can inspect."
  },
  {
    number: "02",
    icon: Layers3,
    title: "The architecture is deep.",
    body:
      "The work spans multi-tenancy, games, wallets, fairness, administration, auditability, risk controls, realtime behavior and operator intelligence."
  },
  {
    number: "03",
    icon: Globe2,
    title: "The market work is beginning.",
    body:
      "The next chapter is about operator conversations, onboarding, legal posture, commercial repeatability and learning from real deployments."
  },
  {
    number: "04",
    icon: Users,
    title: "The team is intentionally small.",
    body:
      "This stage rewards people who want consequential ownership. Roles are being shaped around the system and the mission, not around a giant org chart."
  }
];


const collaborationPaths = [
  {
    id: "core",
    accent: "cyan",
    icon: GitBranch,
    label: "CORE CONTRIBUTOR",
    title: "Own a piece of the machine.",
    body:
      "For builders taking long-term responsibility for architecture, implementation and operational outcomes.",
    bullets: [
      "Clear system ownership",
      "Design reviews and architecture influence",
      "Meaningful contribution scope",
      "Long-term alignment over task volume"
    ]
  },
  {
    id: "advisor",
    accent: "violet",
    icon: FileCheck2,
    label: "STRATEGIC ADVISOR",
    title: "Strengthen decisions that are expensive to get wrong.",
    body:
      "For legal, compliance, operations and market specialists whose judgment can materially improve deployment quality.",
    bullets: [
      "Defined advisory mandate",
      "Direct access to product context",
      "Concrete deliverables and review cadence",
      "Strategic involvement when useful"
    ]
  },
  {
    id: "gtm",
    accent: "green",
    icon: Handshake,
    label: "COMMERCIAL PARTNER",
    title: "Help turn product depth into market pull.",
    body:
      "For operator-facing builders who want to create the sales, onboarding and partnership system around the platform.",
    bullets: [
      "Operator pipeline ownership",
      "Messaging and positioning influence",
      "Pilot and onboarding design",
      "Market feedback into product"
    ]
  },
  {
    id: "capital",
    accent: "pink",
    icon: Rocket,
    label: "CAPITAL PARTNER",
    title: "Add leverage, not just runway.",
    body:
      "For investors whose capital is paired with useful network, hiring, diligence or market-access leverage.",
    bullets: [
      "Selective strategic conversations",
      "Deep technical diligence available",
      "Operator-access leverage valued",
      "Long-term infrastructure thesis"
    ]
  }
];


const tickerItems = [
  "SYSTEMS OVER SURFACE",
  "CORRECTNESS IS A PRODUCT FEATURE",
  "TENANT BOUNDARIES MATTER",
  "EVIDENCE OVER THEATER",
  "PLAYER TRUST MATTERS",
  "OPERATOR CONTROL MATTERS",
  "SMALL TEAM • LARGE OWNERSHIP",
  "BUILD THE OPERATING LAYER"
];


/* ============================================================
   MOTION HELPERS
============================================================ */

const Reveal = ({
  children,
  className = "",
  direction = "up",
  delay = 0
}) => {
  const reduceMotion = useReducedMotion();

  const starts = {
    left: { x: -120, y: 0, rotate: -1.6 },
    right: { x: 120, y: 0, rotate: 1.6 },
    up: { x: 0, y: 74, rotate: 0 },
    topLeft: { x: -150, y: -110, rotate: -2.6 },
    topRight: { x: 150, y: -110, rotate: 2.6 },
    bottomLeft: { x: -150, y: 112, rotate: -2.6 },
    bottomRight: { x: 150, y: 112, rotate: 2.6 }
  };

  const start = starts[direction] || starts.up;

  return (
    <motion.div
      className={className}
      initial={
        reduceMotion
          ? false
          : {
              opacity: 0,
              ...start,
              scale: 0.95,
              filter: "blur(10px)"
            }
      }
      whileInView={{
        opacity: 1,
        x: 0,
        y: 0,
        rotate: 0,
        scale: 1,
        filter: "blur(0px)"
      }}
      viewport={{
        once: true,
        amount: 0.12
      }}
      transition={{
        duration: reduceMotion ? 0 : 0.84,
        delay,
        ease: [0.16, 1, 0.3, 1]
      }}
    >
      {children}
    </motion.div>
  );
};


const Spotlight = ({
  children,
  className = ""
}) => {
  const handlePointerMove = event => {
    const rect = event.currentTarget.getBoundingClientRect();

    event.currentTarget.style.setProperty(
      "--ec12-mx",
      `${event.clientX - rect.left}px`
    );

    event.currentTarget.style.setProperty(
      "--ec12-my",
      `${event.clientY - rect.top}px`
    );
  };

  return (
    <div
      className={`eth-careers-v12-spotlight ${className}`}
      onPointerMove={handlePointerMove}
    >
      <div className="eth-careers-v12-spotlight-glow" />
      {children}
    </div>
  );
};


/* ============================================================
   HERO VISUAL
============================================================ */

const MissionConstellation = ({
  reduceMotion
}) => {
  const nodes = [
    ["SYSTEMS", Server, "cyan"],
    ["LEGAL", Scale, "violet"],
    ["GROWTH", Handshake, "green"],
    ["CAPITAL", Rocket, "pink"]
  ];

  return (
    <div className="eth-careers-v12-constellation">

      <div className="eth-careers-v12-constellation-grid" />
      <div className="eth-careers-v12-constellation-scan" />


      <div className="eth-careers-v12-constellation-top">
        <div>
          <span className="eth-careers-v12-live-dot" />
          FOUNDING LAYER / FORMING
        </div>

        <b>
          SMALL TEAM • HIGH LEVERAGE
        </b>
      </div>


      <div className="eth-careers-v12-constellation-field">

        <motion.div
          className="eth-careers-v12-constellation-ring eth-careers-v12-constellation-ring--outer"
          animate={reduceMotion ? {} : { rotate: 360 }}
          transition={{
            duration: 34,
            repeat: Infinity,
            ease: "linear"
          }}
        />

        <motion.div
          className="eth-careers-v12-constellation-ring eth-careers-v12-constellation-ring--middle"
          animate={reduceMotion ? {} : { rotate: -360 }}
          transition={{
            duration: 24,
            repeat: Infinity,
            ease: "linear"
          }}
        />


        <div className="eth-careers-v12-constellation-core">
          <span className="eth-careers-v12-constellation-wave" />

          <Orbit size={30} strokeWidth={1.6} />

          <small>
            ETHOSINO
          </small>

          <strong>
            CORE TEAM
          </strong>

          <b>
            BUILD THE SYSTEM
          </b>
        </div>


        {nodes.map(
          (
            node,
            index
          ) => {
            const Icon = node[1];

            return (
              <motion.div
                key={node[0]}
                className={[
                  "eth-careers-v12-constellation-node",
                  `eth-careers-v12-constellation-node--${index + 1}`,
                  `eth-careers-v12-constellation-node--${node[2]}`
                ].join(" ")}
                animate={
                  reduceMotion
                    ? {}
                    : {
                        y: [
                          0,
                          index % 2 === 0 ? -9 : 9,
                          0
                        ],
                        x: [
                          0,
                          index < 2 ? 5 : -5,
                          0
                        ]
                      }
                }
                transition={{
                  duration: 4.7 + index * 0.34,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <span>
                  <Icon size={16} />
                </span>

                <div>
                  <small>
                    TRACK 0{index + 1}
                  </small>

                  <strong>
                    {node[0]}
                  </strong>
                </div>
              </motion.div>
            );
          }
        )}


        <span className="eth-careers-v12-constellation-packet eth-careers-v12-constellation-packet--one" />
        <span className="eth-careers-v12-constellation-packet eth-careers-v12-constellation-packet--two" />
        <span className="eth-careers-v12-constellation-packet eth-careers-v12-constellation-packet--three" />

      </div>


      <div className="eth-careers-v12-constellation-bottom">

        <div>
          <span>
            PRODUCT
          </span>

          <strong>
            BUILT + EVOLVING
          </strong>
        </div>

        <ArrowRight size={16} />

        <div>
          <span>
            NEXT
          </span>

          <strong>
            TEAM + MARKET
          </strong>
        </div>

        <b>
          ALIGNMENT FIRST
        </b>

      </div>

    </div>
  );
};


/* ============================================================
   PAGE
============================================================ */

const OperatorCareersHelper = () => {
  const reduceMotion = useReducedMotion();

  const [
    activeRole,
    setActiveRole
  ] = useState(0);

  const [
    activeCollaboration,
    setActiveCollaboration
  ] = useState(0);


  const currentRole =
    roleTracks[activeRole];

  const CurrentRoleIcon =
    currentRole.icon;


  const currentCollaboration =
    collaborationPaths[activeCollaboration];

  const CurrentCollaborationIcon =
    currentCollaboration.icon;


  const {
    scrollYProgress
  } = useScroll();


  const progress =
    useSpring(
      scrollYProgress,
      {
        stiffness: 120,
        damping: 28,
        restDelta: 0.001
      }
    );


  const heroY =
    useTransform(
      scrollYProgress,
      [0, 0.16],
      [0, reduceMotion ? 0 : 70]
    );


  const heroOpacity =
    useTransform(
      scrollYProgress,
      [0, 0.13],
      [1, 0.77]
    );


  const heroStats =
    useMemo(
      () => [
        ["STAGE", "Product built"],
        ["TEAM", "Intentionally small"],
        ["WORK", "High ownership"],
        ["MISSION", "Operator-grade infrastructure"]
      ],
      []
    );


  return (
    <Fragment>

      <motion.div
        className="eth-careers-v12-scroll-progress"
        style={{
          scaleX: progress
        }}
      />


      <HeaderToMainPage />


      <main
        className="eth-careers-v12-page"
        id="ethosino-careers-v12"
      >

        {/* ====================================================
            ATMOSPHERE
        ==================================================== */}

        <div
          className="eth-careers-v12-atmosphere"
          aria-hidden="true"
        >
          <div className="eth-careers-v12-atmosphere-grid" />
          <div className="eth-careers-v12-atmosphere-noise" />

          <div className="eth-careers-v12-orb eth-careers-v12-orb--cyan" />
          <div className="eth-careers-v12-orb eth-careers-v12-orb--green" />
          <div className="eth-careers-v12-orb eth-careers-v12-orb--violet" />
          <div className="eth-careers-v12-orb eth-careers-v12-orb--pink" />
        </div>


        {/* ====================================================
            HERO
        ==================================================== */}

        <section style={{ marginTop: 50 }} className="eth-careers-v12-hero">

          <motion.div
            className="eth-careers-v12-hero-parallax"
            style={{
              y: heroY,
              opacity: heroOpacity
            }}
          >

            <div className="eth-careers-v12-shell eth-careers-v12-hero-grid">

              <div className="eth-careers-v12-hero-copy">

                <motion.div
                  className="eth-careers-v12-system-badge"
                  initial={
                    reduceMotion
                      ? false
                      : {
                          opacity: 0,
                          x: -38
                        }
                  }
                  animate={{
                    opacity: 1,
                    x: 0
                  }}
                  transition={{
                    duration: 0.58
                  }}
                >
                  <span className="eth-careers-v12-live-dot" />

                  CAREERS + STRATEGIC ROLES

                  <i />

                  FOUNDING LAYER
                </motion.div>


                <motion.h1
                  initial={
                    reduceMotion
                      ? false
                      : {
                          opacity: 0,
                          x: -115,
                          y: 46,
                          rotate: -2
                        }
                  }
                  animate={{
                    opacity: 1,
                    x: 0,
                    y: 0,
                    rotate: 0
                  }}
                  transition={{
                    duration: 0.88,
                    delay: 0.04,
                    ease: [0.16, 1, 0.3, 1]
                  }}
                >
                  Help build the company{" "}

                  <span>
                    behind the operating layer.
                  </span>
                </motion.h1>


                <motion.p
                  className="eth-careers-v12-hero-lead"
                  initial={
                    reduceMotion
                      ? false
                      : {
                          opacity: 0,
                          y: 30
                        }
                  }
                  animate={{
                    opacity: 1,
                    y: 0
                  }}
                  transition={{
                    duration: 0.72,
                    delay: 0.14
                  }}
                >
                  Ethosino is not assembling a large organization around a
                  prototype. The product exists. The next stage is building the
                  small group of people who can make the platform harder to
                  break, easier to operate, easier to defend, and easier to put
                  in front of serious operators.
                </motion.p>


                <motion.div
                  className="eth-careers-v12-hero-thesis"
                  initial={
                    reduceMotion
                      ? false
                      : {
                          opacity: 0,
                          x: -46
                        }
                  }
                  animate={{
                    opacity: 1,
                    x: 0
                  }}
                  transition={{
                    duration: 0.67,
                    delay: 0.24
                  }}
                >
                  <Sparkles size={18} />

                  <span>
                    We are looking for people who want{" "}
                    <strong>
                      consequential ownership
                    </strong>{" "}
                    — not another narrow job description inside a machine somebody
                    else already designed.
                  </span>
                </motion.div>


                <motion.div
                  className="eth-careers-v12-hero-actions"
                  initial={
                    reduceMotion
                      ? false
                      : {
                          opacity: 0,
                          y: 24
                        }
                  }
                  animate={{
                    opacity: 1,
                    y: 0
                  }}
                  transition={{
                    duration: 0.62,
                    delay: 0.31
                  }}
                >

                  <a
                    href="#roles"
                    className="eth-careers-v12-btn eth-careers-v12-btn--primary"
                  >
                    Explore strategic roles
                    <ArrowRight size={17} />
                  </a>


                  <Link
                    to="/contact"
                    className="eth-careers-v12-btn eth-careers-v12-btn--ghost"
                  >
                    Start a conversation
                  </Link>

                </motion.div>


                <div className="eth-careers-v12-hero-proofline">

                  <span>
                    <Server size={14} />
                    systems engineering
                  </span>

                  <span>
                    <Scale size={14} />
                    legal + compliance
                  </span>

                  <span>
                    <Handshake size={14} />
                    operator GTM
                  </span>

                  <span>
                    <Rocket size={14} />
                    strategic capital
                  </span>

                </div>

              </div>


              <motion.div
                className="eth-careers-v12-hero-visual"
                initial={
                  reduceMotion
                    ? false
                    : {
                        opacity: 0,
                        x: 120,
                        y: -64,
                        rotate: 2.7,
                        scale: 0.94
                      }
                }
                animate={{
                  opacity: 1,
                  x: 0,
                  y: 0,
                  rotate: 0,
                  scale: 1
                }}
                transition={{
                  duration: 0.98,
                  delay: 0.12,
                  ease: [0.16, 1, 0.3, 1]
                }}
              >

                <MissionConstellation
                  reduceMotion={reduceMotion}
                />


                <motion.div
                  className="eth-careers-v12-float eth-careers-v12-float--one"
                  animate={
                    reduceMotion
                      ? {}
                      : {
                          y: [0, -12, 0],
                          rotate: [-1, 1, -1]
                        }
                  }
                  transition={{
                    duration: 5.6,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <BriefcaseBusiness size={15} />

                  <div>
                    <span>
                      HIRING MODE
                    </span>

                    <strong>
                      SELECTIVE
                    </strong>
                  </div>
                </motion.div>


                <motion.div
                  className="eth-careers-v12-float eth-careers-v12-float--two"
                  animate={
                    reduceMotion
                      ? {}
                      : {
                          y: [0, 11, 0],
                          rotate: [1, -1, 1]
                        }
                  }
                  transition={{
                    duration: 6.4,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <KeyRound size={15} />

                  <div>
                    <span>
                      OWNERSHIP
                    </span>

                    <strong>
                      FOUNDATIONAL
                    </strong>
                  </div>
                </motion.div>

              </motion.div>

            </div>


            <div className="eth-careers-v12-shell">

              <motion.div
                className="eth-careers-v12-metric-strip"
                initial={
                  reduceMotion
                    ? false
                    : {
                        opacity: 0,
                        y: 30
                      }
                }
                animate={{
                  opacity: 1,
                  y: 0
                }}
                transition={{
                  duration: 0.7,
                  delay: 0.42
                }}
              >

                {heroStats.map(
                  (
                    item,
                    index
                  ) => (
                    <div
                      key={item[0]}
                      className="eth-careers-v12-metric"
                    >
                      <span>
                        0{index + 1} / {item[0]}
                      </span>

                      <strong>
                        {item[1]}
                      </strong>
                    </div>
                  )
                )}

              </motion.div>

            </div>

          </motion.div>


          <div className="eth-careers-v12-ticker">

            <div className="eth-careers-v12-ticker-track">

              {[...tickerItems, ...tickerItems].map(
                (
                  item,
                  index
                ) => (
                  <React.Fragment key={`${item}-${index}`}>
                    <span>
                      {item}
                    </span>

                    <i>
                      ◆
                    </i>
                  </React.Fragment>
                )
              )}

            </div>

          </div>

        </section>


        {/* ====================================================
            SUBNAV
        ==================================================== */}

        <nav className="eth-careers-v12-subnav">

          <div className="eth-careers-v12-shell eth-careers-v12-subnav-inner">

            <div className="eth-careers-v12-subnav-brand">
              <span />
              FOUNDING LAYER
            </div>


            <div className="eth-careers-v12-subnav-links">

              <a href="#stage">
                Stage
              </a>

              <a href="#roles">
                Roles
              </a>

              <a href="#principles">
                Principles
              </a>

              <a href="#collaboration">
                Collaboration
              </a>

              <a href="#not">
                What this is not
              </a>

              <Link
                className="eth-careers-v12-subnav-cta"
                to="/contact"
              >
                Talk
              </Link>

            </div>

          </div>

        </nav>


        {/* ====================================================
            STAGE
        ==================================================== */}

        <section
          className="eth-careers-v12-section"
          id="stage"
        >

          <div className="eth-careers-v12-shell">

            <Reveal
              direction="topLeft"
              className="eth-careers-v12-section-head eth-careers-v12-section-head--split"
            >

              <div>
                <span className="eth-careers-v12-kicker">
                  WHY NOW
                </span>

                <h2>
                  Join after the idea stage.{" "}

                  <span>
                    Before the organization calcifies.
                  </span>
                </h2>
              </div>

              <p>
                There is enough product to evaluate and enough company left to
                shape. That is the window: architecture exists, the operating
                philosophy is clear, and the next contributors can still have
                disproportionate influence over how Ethosino matures.
              </p>

            </Reveal>


            <div className="eth-careers-v12-stage-grid">

              {stageCards.map(
                (
                  card,
                  index
                ) => {
                  const Icon =
                    card.icon;

                  const directions = [
                    "topLeft",
                    "topRight",
                    "bottomLeft",
                    "bottomRight"
                  ];

                  return (
                    <Reveal
                      key={card.title}
                      direction={directions[index]}
                      delay={index * 0.045}
                    >
                      <Spotlight className="eth-careers-v12-stage-card">

                        <div className="eth-careers-v12-stage-card-top">
                          <span>
                            {card.number}
                          </span>

                          <Icon size={21} />
                        </div>

                        <h3>
                          {card.title}
                        </h3>

                        <p>
                          {card.body}
                        </p>

                      </Spotlight>
                    </Reveal>
                  );
                }
              )}

            </div>


            <Reveal
              direction="bottomRight"
              className="eth-careers-v12-stage-manifesto"
            >

              <Waypoints size={23} />

              <div>
                <span>
                  THE WINDOW
                </span>

                <strong>
                  Mature enough to inspect. Early enough to matter.
                </strong>
              </div>

            </Reveal>

          </div>
        </section>


        {/* ====================================================
            ROLES
        ==================================================== */}

        <section
          className="eth-careers-v12-section eth-careers-v12-section--panel"
          id="roles"
        >

          <div className="eth-careers-v12-shell">

            <Reveal
              direction="topRight"
              className="eth-careers-v12-section-head eth-careers-v12-section-head--split"
            >

              <div>
                <span className="eth-careers-v12-kicker">
                  OPEN STRATEGIC TRACKS
                </span>

                <h2>
                  Four ways to{" "}

                  <span>
                    change the trajectory.
                  </span>
                </h2>
              </div>

              <p>
                These are not interchangeable seats. Each track owns a different
                expensive problem: technical integrity, legal defensibility,
                market distribution or company leverage.
              </p>

            </Reveal>


            <div className="eth-careers-v12-role-layout">

              <Reveal
                direction="left"
                className="eth-careers-v12-role-tabs"
              >

                {roleTracks.map(
                  (
                    role,
                    index
                  ) => {
                    const Icon =
                      role.icon;

                    return (
                      <motion.button
                        key={role.id}
                        type="button"
                        className={[
                          "eth-careers-v12-role-tab",
                          `eth-careers-v12-role-tab--${role.accent}`,
                          activeRole === index
                            ? "is-active"
                            : ""
                        ].join(" ")}
                        onClick={() =>
                          setActiveRole(index)
                        }
                        whileHover={{
                          x: 5
                        }}
                        whileTap={{
                          scale: 0.985
                        }}
                      >

                        <span className="eth-careers-v12-role-tab-index">
                          {role.number}
                        </span>

                        <span className="eth-careers-v12-role-tab-icon">
                          <Icon size={19} />
                        </span>

                        <span className="eth-careers-v12-role-tab-copy">
                          <small>
                            {role.badge}
                          </small>

                          <strong>
                            {role.title}
                          </strong>

                          <em>
                            {role.tagline}
                          </em>
                        </span>

                        <ChevronRight size={16} />

                      </motion.button>
                    );
                  }
                )}

              </Reveal>


              <Reveal
                direction="right"
                className="eth-careers-v12-role-detail-wrap"
              >

                <AnimatePresence mode="wait">

                  <motion.article
                    key={currentRole.id}
                    className={`eth-careers-v12-role-detail eth-careers-v12-role-detail--${currentRole.accent}`}
                    initial={
                      reduceMotion
                        ? false
                        : {
                            opacity: 0,
                            x: 80,
                            y: -30,
                            rotate: 1.5,
                            filter: "blur(8px)"
                          }
                    }
                    animate={{
                      opacity: 1,
                      x: 0,
                      y: 0,
                      rotate: 0,
                      filter: "blur(0px)"
                    }}
                    exit={
                      reduceMotion
                        ? undefined
                        : {
                            opacity: 0,
                            x: -65,
                            y: 24,
                            rotate: -1.2,
                            filter: "blur(7px)"
                          }
                    }
                    transition={{
                      duration: 0.34
                    }}
                  >

                    <div className="eth-careers-v12-role-detail-top">

                      <span className="eth-careers-v12-role-big-index">
                        {currentRole.number}
                      </span>

                      <div className="eth-careers-v12-role-detail-icon">
                        <CurrentRoleIcon size={29} />
                      </div>

                    </div>


                    <small>
                      {currentRole.badge}
                    </small>

                    <h3>
                      {currentRole.title}
                    </h3>

                    <p className="eth-careers-v12-role-tagline">
                      {currentRole.tagline}
                    </p>

                    <p className="eth-careers-v12-role-summary">
                      {currentRole.summary}
                    </p>


                    <div className="eth-careers-v12-role-signals">

                      {currentRole.signals.map(
                        signal => (
                          <div key={signal[0]}>
                            <span>
                              {signal[0]}
                            </span>

                            <strong>
                              {signal[1]}
                            </strong>
                          </div>
                        )
                      )}

                    </div>


                    <div className="eth-careers-v12-role-columns">

                      <div>
                        <span className="eth-careers-v12-role-minihead">
                          WHAT YOU INFLUENCE
                        </span>

                        <div className="eth-careers-v12-role-list">

                          {currentRole.influence.map(
                            item => (
                              <span key={item}>
                                <Check size={12} />
                                {item}
                              </span>
                            )
                          )}

                        </div>
                      </div>


                      <div>
                        <span className="eth-careers-v12-role-minihead">
                          WHAT WE VALUE
                        </span>

                        <div className="eth-careers-v12-role-list">

                          {currentRole.lookingFor.map(
                            item => (
                              <span key={item}>
                                <BadgeCheck size={12} />
                                {item}
                              </span>
                            )
                          )}

                        </div>
                      </div>

                    </div>


                    <div className="eth-careers-v12-role-collaboration">

                      <div>
                        <span>
                          COLLABORATION MODEL
                        </span>

                        <p>
                          {currentRole.collaboration}
                        </p>
                      </div>

                      <Link
                        to={currentRole.href}
                        className="eth-careers-v12-btn eth-careers-v12-btn--primary"
                      >
                        {currentRole.cta}
                        <ArrowRight size={15} />
                      </Link>

                    </div>

                  </motion.article>

                </AnimatePresence>

              </Reveal>

            </div>

          </div>
        </section>


        {/* ====================================================
            PRINCIPLES
        ==================================================== */}

        <section
          className="eth-careers-v12-section"
          id="principles"
        >

          <div className="eth-careers-v12-shell">

            <Reveal
              direction="bottomLeft"
              className="eth-careers-v12-section-head"
            >

              <span className="eth-careers-v12-kicker">
                HOW WE THINK
              </span>

              <h2>
                The work is technical.{" "}

                <span>
                  The standards are cultural.
                </span>
              </h2>

              <p>
                Ethosino deals with money, probability, player behavior and
                operator authority. The quality bar cannot stop at “it works on
                my machine.”
              </p>

            </Reveal>


            <div className="eth-careers-v12-principles-grid">

              {principles.map(
                (
                  principle,
                  index
                ) => {
                  const Icon =
                    principle.icon;

                  const directions = [
                    "topLeft",
                    "topRight",
                    "left",
                    "right",
                    "bottomLeft",
                    "bottomRight"
                  ];

                  return (
                    <Reveal
                      key={principle.title}
                      direction={directions[index]}
                      delay={index * 0.035}
                    >
                      <Spotlight className="eth-careers-v12-principle-card">

                        <div className="eth-careers-v12-principle-card-top">

                          <span>
                            0{index + 1}
                          </span>

                          <Icon size={20} />

                        </div>

                        <h3>
                          {principle.title}
                        </h3>

                        <p>
                          {principle.body}
                        </p>

                      </Spotlight>
                    </Reveal>
                  );
                }
              )}

            </div>


            <Reveal
              direction="right"
              className="eth-careers-v12-principle-manifesto"
            >

              <Fingerprint size={22} />

              <div>
                <span>
                  THE STANDARD
                </span>

                <strong>
                  If the system matters, the explanation matters too.
                </strong>
              </div>

            </Reveal>

          </div>
        </section>


        {/* ====================================================
            COLLABORATION
        ==================================================== */}

        <section
          className="eth-careers-v12-section eth-careers-v12-section--panel"
          id="collaboration"
        >

          <div className="eth-careers-v12-shell">

            <Reveal
              direction="topRight"
              className="eth-careers-v12-section-head eth-careers-v12-section-head--split"
            >

              <div>
                <span className="eth-careers-v12-kicker">
                  COLLABORATION STRUCTURE
                </span>

                <h2>
                  Not every high-leverage person{" "}

                  <span>
                    needs the same relationship.
                  </span>
                </h2>
              </div>

              <p>
                The right structure depends on what you own, how often you are
                involved and how directly your work changes the company's
                execution. The common requirement is meaningful scope and clear
                accountability.
              </p>

            </Reveal>


            <div className="eth-careers-v12-collab-layout">

              <Reveal
                direction="left"
                className="eth-careers-v12-collab-tabs"
              >

                {collaborationPaths.map(
                  (
                    path,
                    index
                  ) => {
                    const Icon =
                      path.icon;

                    return (
                      <motion.button
                        key={path.id}
                        type="button"
                        className={[
                          "eth-careers-v12-collab-tab",
                          `eth-careers-v12-collab-tab--${path.accent}`,
                          activeCollaboration === index
                            ? "is-active"
                            : ""
                        ].join(" ")}
                        onClick={() =>
                          setActiveCollaboration(index)
                        }
                        whileHover={{
                          x: 5
                        }}
                      >

                        <span>
                          0{index + 1}
                        </span>

                        <div>
                          <Icon size={18} />
                        </div>

                        <section>
                          <small>
                            {path.label}
                          </small>

                          <strong>
                            {path.title}
                          </strong>
                        </section>

                        <ChevronRight size={15} />

                      </motion.button>
                    );
                  }
                )}

              </Reveal>


              <Reveal
                direction="bottomRight"
                className="eth-careers-v12-collab-detail-wrap"
              >

                <AnimatePresence mode="wait">

                  <motion.article
                    key={currentCollaboration.id}
                    className={`eth-careers-v12-collab-detail eth-careers-v12-collab-detail--${currentCollaboration.accent}`}
                    initial={
                      reduceMotion
                        ? false
                        : {
                            opacity: 0,
                            x: 78,
                            y: -28,
                            rotate: 1.4,
                            filter: "blur(8px)"
                          }
                    }
                    animate={{
                      opacity: 1,
                      x: 0,
                      y: 0,
                      rotate: 0,
                      filter: "blur(0px)"
                    }}
                    exit={
                      reduceMotion
                        ? undefined
                        : {
                            opacity: 0,
                            x: -60,
                            y: 22,
                            rotate: -1.2,
                            filter: "blur(6px)"
                          }
                    }
                    transition={{
                      duration: 0.33
                    }}
                  >

                    <div className="eth-careers-v12-collab-detail-head">
                      <CurrentCollaborationIcon size={29} />

                      <div>
                        <span>
                          {currentCollaboration.label}
                        </span>

                        <strong>
                          {currentCollaboration.title}
                        </strong>
                      </div>
                    </div>


                    <p>
                      {currentCollaboration.body}
                    </p>


                    <div className="eth-careers-v12-collab-bullets">

                      {currentCollaboration.bullets.map(
                        item => (
                          <span key={item}>
                            <Check size={12} />
                            {item}
                          </span>
                        )
                      )}

                    </div>


                    <div className="eth-careers-v12-collab-outcome">
                      <span>
                        COMMON DENOMINATOR
                      </span>

                      <strong>
                        Clear ownership. Honest expectations. Long-term usefulness.
                      </strong>
                    </div>

                  </motion.article>

                </AnimatePresence>

              </Reveal>

            </div>

          </div>
        </section>


        {/* ====================================================
            NOT THIS
        ==================================================== */}

        <section
          className="eth-careers-v12-section"
          id="not"
        >

          <div className="eth-careers-v12-shell">

            <Reveal
              direction="topLeft"
              className="eth-careers-v12-section-head"
            >

              <span className="eth-careers-v12-kicker">
                CLARITY BEFORE OUTREACH
              </span>

              <h2>
                This page is{" "}

                <span>
                  intentionally not for everyone.
                </span>
              </h2>

              <p>
                Saying what Ethosino is not looking for saves both sides time.
                The company is building a small, high-context founding layer
                around a technically deep product.
              </p>

            </Reveal>


            <Reveal
              direction="bottomRight"
              className="eth-careers-v12-not-card"
            >

              <div className="eth-careers-v12-not-grid">

                {[
                  "Not a mass hiring campaign",
                  "Not a junior-role training program",
                  "Not a generic outsourced development shop",
                  "Not a passive advisory-logo collection",
                  "Not a growth-at-all-costs sales organization",
                  "Not a place where critical systems are somebody else's problem"
                ].map(
                  item => (
                    <div key={item}>
                      <X size={14} />
                      <span>
                        {item}
                      </span>
                    </div>
                  )
                )}

              </div>


              <div className="eth-careers-v12-not-foot">

                <div>
                  <span>
                    BETTER FIT
                  </span>

                  <strong>
                    You want your work to change architecture, judgment or distribution — and you are comfortable being accountable for the result.
                  </strong>
                </div>

                <ArrowRight size={22} />

              </div>

            </Reveal>

          </div>
        </section>


        {/* ====================================================
            MISSION
        ==================================================== */}

        <section className="eth-careers-v12-section eth-careers-v12-section--panel">

          <div className="eth-careers-v12-shell">

            <Reveal
              direction="bottomLeft"
              className="eth-careers-v12-mission"
            >

              <div className="eth-careers-v12-mission-icon">
                <HeartHandshake size={28} />
              </div>


              <div className="eth-careers-v12-mission-copy">
                <span>
                  WHY THE COMPANY EXISTS
                </span>

                <h2>
                  Build gaming infrastructure{" "}

                  <strong>
                    worth explaining.
                  </strong>
                </h2>

                <p>
                  Ethosino was built around the idea that operators should have
                  more control, players should have stronger evidence, and the
                  systems connecting games, money and policy should be easier to
                  inspect rather than easier to hide behind.
                </p>
              </div>


              <div className="eth-careers-v12-mission-principles">

                <span>
                  <Check size={12} />
                  fair where proof can exist
                </span>

                <span>
                  <Check size={12} />
                  explicit where authority lives
                </span>

                <span>
                  <Check size={12} />
                  accountable where money moves
                </span>

                <span>
                  <Check size={12} />
                  useful to serious operators
                </span>

              </div>

            </Reveal>

          </div>
        </section>


        {/* ====================================================
            FINAL CTA
        ==================================================== */}

        <section className="eth-careers-v12-final">

          <div className="eth-careers-v12-shell">

            <Reveal
              direction="bottomRight"
              className="eth-careers-v12-final-card"
            >

              <div className="eth-careers-v12-final-grid" />
              <div className="eth-careers-v12-final-beam" />


              <div className="eth-careers-v12-final-copy">

                <span>
                  IF YOU SEE THE SYSTEM, NOT JUST THE JOB —
                </span>

                <h2>
                  There may be a place to{" "}

                  <strong>
                    build it with us.
                  </strong>
                </h2>

                <p>
                  Send a concise note about the track you fit, what you have
                  owned before, and what part of Ethosino you think you could
                  materially improve. Specificity beats a generic résumé drop.
                </p>


                <div className="eth-careers-v12-final-actions">

                  <Link
                    to="/contact"
                    className="eth-careers-v12-btn eth-careers-v12-btn--primary eth-careers-v12-btn--large"
                  >
                    Start a strategic conversation
                    <ArrowRight size={18} />
                  </Link>


                  <Link
                    to="/company-brief"
                    className="eth-careers-v12-btn eth-careers-v12-btn--ghost eth-careers-v12-btn--large"
                  >
                    Request company brief
                  </Link>

                </div>

              </div>


              <div className="eth-careers-v12-final-checklist">

                <div className="eth-careers-v12-final-checklist-head">
                  <Network size={21} />

                  <div>
                    <span>
                      GOOD FIRST MESSAGE
                    </span>

                    <strong>
                      Give us signal.
                    </strong>
                  </div>
                </div>


                <div className="eth-careers-v12-final-checklist-list">

                  <span>
                    <Check size={13} />
                    Which role / track fits
                  </span>

                  <span>
                    <Check size={13} />
                    What you have actually owned
                  </span>

                  <span>
                    <Check size={13} />
                    Why this stage interests you
                  </span>

                  <span>
                    <Check size={13} />
                    What you would improve first
                  </span>

                  <span>
                    <Check size={13} />
                    Your realistic level of involvement
                  </span>

                </div>

              </div>

            </Reveal>

          </div>
        </section>

      </main>


      <Footer />

    </Fragment>
  );
};


export default OperatorCareersHelper;
